/**
 * Whether an AR (geolocation) level is tied to a place or playable anywhere.
 *
 * The Copilot asks the client which they want, but the brief had nowhere to
 * put the answer and the generator builds play-anywhere only, so a hunt the
 * client wanted at their store opened as play-anywhere, and the reverse when
 * the generator sent no point areas at all (AI sheet rows 37, 41). The brief
 * now carries `location_mode`, and the agreed mode is written onto the draft.
 */

export type LocationMode = 'fixed' | 'anywhere';

const FIXED = /\b(?:fixed|location[\s_-]*(?:fixed|bound|based)|on[\s_-]*(?:site|location)|venue|store|in[\s_-]*store)\b|ثابت|موقع\s*محدد|مكان\s*محدد/i;
const ANYWHERE = /any[\s_-]*where|\b(?:free[\s_-]*play|remote|online)\b|أي\s*مكان|اي\s*مكان/i;

/** The mode a brief level states, in any of the ways the model writes it; null when it states none. */
export function locationModeOf(level: any): LocationMode | null {
  if (!level || typeof level !== 'object') return null;
  const flag = level.is_location_fixed ?? level.location_fixed;
  if (flag === true || flag === 'true') return 'fixed';
  if (flag === false || flag === 'false') return 'anywhere';
  if (level.play_anywhere === true || level.play_anywhere === 'true') return 'anywhere';
  const said = [level.location_mode, level.mode, level.location_type]
    .find((v) => typeof v === 'string' && v.trim());
  if (!said) return null;
  if (ANYWHERE.test(said)) return 'anywhere';
  if (FIXED.test(said)) return 'fixed';
  return null;
}

/** point_areas as an object: the generator has sent it as a JSON string. */
export function pointAreasOf(level: any): any | null {
  const raw = level?.point_areas ?? level?.geolocation_data ?? null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return raw && typeof raw === 'object' ? raw : null;
}

/** False only when the level says play-anywhere; absent means fixed, as every older contest is. */
export function isFixedLocation(pointAreas: any, level?: any): boolean {
  const flag = pointAreas?.is_location_fixed ?? level?.is_location_fixed;
  return !(flag === false || flag === 'false' || flag === 0);
}

const coord = (v: any, limit: number): number | null => {
  const n = typeof v === 'string' && v.trim() ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) && Math.abs(n) <= limit ? n : null;
};

/**
 * Writes each brief geolocation level's mode onto the generated one, in order.
 * A fixed level takes the places the client named (name and coordinates); a
 * play-anywhere level keeps a single point, as the form builds it.
 */
export function applyLocationModes(generated: any[], briefLevels: any[]): void {
  if (!Array.isArray(generated) || !Array.isArray(briefLevels)) return;
  const isGeo = (l: any) => String(l?.level_type ?? l?.type ?? '').toLowerCase() === 'geolocation';
  const agreed = briefLevels.filter(isGeo);
  generated.filter(isGeo).forEach((level, i) => {
    const mode = locationModeOf(agreed[i]);
    if (!mode) return;
    const areas = pointAreasOf(level) ?? { type: 'multi_point', points: [] };
    areas.is_location_fixed = mode === 'fixed';
    const points: any[] = Array.isArray(areas.points) ? areas.points : [];
    if (mode === 'anywhere') {
      areas.points = points.slice(0, 1);
    } else {
      const places = Array.isArray(agreed[i]?.points) ? agreed[i].points : [];
      places.forEach((place: any, p: number) => {
        const point = points[p] ?? (points[p] = { prize: { type: 'name' } });
        const name = [place?.name, place?.location_name, place?.venue]
          .find((v) => typeof v === 'string' && v.trim());
        if (name) point.location_name = name.trim();
        const lat = coord(place?.lat ?? place?.latitude, 90);
        const lng = coord(place?.lng ?? place?.lon ?? place?.longitude, 180);
        if (lat !== null && lng !== null) point.location = { ...(point.location || {}), lat, lng };
      });
      areas.points = points;
    }
    level.point_areas = areas;
  });
}
